/**
 * Session recording — one `MediaRecorder` per player, streamed to the server.
 *
 * Adapted from Craft Together's `useAudioRecorder.ts`. The structure is theirs
 * and so is every comment that explains a failure; what changed is that the
 * ordering and container logic moved to `recorderCore.ts` where it can be
 * tested, and that the stream recorded is the beep bus rather than the bare
 * microphone (see `syncBeep.ts`).
 *
 * The recorder shares the microphone `MediaStream` with WebRTC rather than
 * calling `getUserMedia` again. Two independent captures of one device is how
 * you get two different gain settings and, on some hardware, a second capture
 * that fails outright.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { ChunkSink } from './recorderCore'
import { createChunkChain, newRecordingId, pickRecorderMimeType } from './recorderCore'

/** `MediaRecorder`'s timeslice. One second, as in Craft Together. */
const TIMESLICE_MS = 1000
/**
 * How long `stop()` may take before we give up waiting.
 *
 * Safari can deadlock the recorder when the microphone track is pulled out from
 * under it mid-stop. Without this the end-of-round path would wait forever and
 * the end screen would never appear — the recording is already in trouble at
 * that point, and the server's integrity flags will say so.
 */
const STOP_TIMEOUT_MS = 5000

export interface RecorderState {
  recording: boolean
  /** Set when the browser refused every container and the default too. */
  error: string | null
  mimeType: string | undefined
}

export interface RecorderAccounting {
  chunkCount: number
  lastSeq: number
  mimeType?: string
  /** The run these chunks belong to. See `newRecordingId`. */
  recordingId?: string
}

export interface AudioRecorder extends RecorderState {
  /**
   * Start a new recorder run. `from` overrides the stream the hook was given —
   * the caller's way of recording the bare microphone when the beep bus is not
   * running.
   */
  start(from?: MediaStream | null): Promise<void>
  /** Resolves once every chunk has been sent; returns the client's accounting. */
  stop(): Promise<RecorderAccounting>
}

export function useAudioRecorder(stream: MediaStream | null, onChunk: ChunkSink): AudioRecorder {
  const recorderRef = useRef<MediaRecorder | null>(null)
  /** The current run's id; a fresh one every `start`. */
  const runRef = useRef<string | undefined>(undefined)
  const chainRef = useRef(createChunkChain(onChunk))
  const sinkRef = useRef(onChunk)
  const [state, setState] = useState<RecorderState>({
    recording: false,
    error: null,
    mimeType: undefined,
  })

  // The sink is a prop and may be re-created every render; the chain must not
  // be, or a re-render mid-round would restart the sequence at zero and the
  // server would see every seq twice.
  sinkRef.current = onChunk
  useEffect(() => {
    chainRef.current = createChunkChain((payload) => sinkRef.current(payload))
    // Deliberately once: the chain lives as long as the component.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const start = useCallback(async (from?: MediaStream | null): Promise<void> => {
    const source = from ?? stream
    if (!source) return
    if (recorderRef.current && recorderRef.current.state !== 'inactive') return
    if (typeof MediaRecorder === 'undefined') {
      setState((s) => ({ ...s, error: "This browser can't record audio." }))
      return
    }

    const mimeType = pickRecorderMimeType((candidate) => MediaRecorder.isTypeSupported(candidate))

    let recorder: MediaRecorder
    try {
      recorder = new MediaRecorder(source, mimeType ? { mimeType } : undefined)
    } catch (err) {
      setState((s) => ({ ...s, error: `Couldn't start recording: ${(err as Error).message}` }))
      return
    }

    // A new run: new id, seq from 0. The server files it as its own segment,
    // so a recorder restarted by a reload never splices into the first file.
    const recordingId = newRecordingId()
    runRef.current = recordingId
    const chain = createChunkChain((payload) => sinkRef.current({ ...payload, recordingId }), mimeType)
    chainRef.current = chain

    recorder.ondataavailable = (event) => {
      // Capture the blob synchronously. The chain assigns its seq now and
      // awaits the conversion later, which is what keeps the order true.
      if (event.data && event.data.size > 0) chain.push(event.data)
    }

    recorder.start(TIMESLICE_MS)
    recorderRef.current = recorder
    setState({ recording: true, error: null, mimeType })
  }, [stream])

  const stop = useCallback(async (): Promise<RecorderAccounting> => {
    const recorder = recorderRef.current
    const chain = chainRef.current
    const mimeType = state.mimeType
    const recordingId = runRef.current

    if (!recorder || recorder.state === 'inactive') {
      setState((s) => ({ ...s, recording: false }))
      await chain.drain()
      return { ...chain.accounting(), mimeType, recordingId }
    }

    await new Promise<void>((resolve) => {
      let settled = false
      let timer: ReturnType<typeof setTimeout> | null = null
      const finish = (): void => {
        if (settled) return
        settled = true
        if (timer) clearTimeout(timer)
        resolve()
      }

      recorder.onstop = () => {
        // `ondataavailable` for the final chunk fires immediately *before*
        // `onstop`, so defer one turn to let it join the chain before we wait
        // on the tail — otherwise the last second of audio is dropped.
        setTimeout(() => {
          void chain.drain().then(finish)
        }, 0)
      }

      timer = setTimeout(() => {
        console.warn('[audio] stop timed out; reporting what was sent')
        finish()
      }, STOP_TIMEOUT_MS)

      try {
        recorder.stop()
      } catch (err) {
        console.warn('[audio] recorder.stop() threw:', err)
        finish()
      }
    })

    recorderRef.current = null
    setState((s) => ({ ...s, recording: false }))
    await chain.drain()
    return { ...chain.accounting(), mimeType, recordingId }
  }, [state.mimeType])

  // A tab that closes mid-round should not take the recording with it. The
  // chunks already sent are on the server; this only stops the device.
  useEffect(
    () => () => {
      const recorder = recorderRef.current
      if (recorder && recorder.state !== 'inactive') {
        try {
          recorder.stop()
        } catch {
          // Already gone.
        }
      }
    },
    [],
  )

  return { ...state, start, stop }
}
